import React from 'react';
import {
    List,
} from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import {
    AppPageForm, onFieldChange, onReactSelectChanged, toasty,
} from '../common/forms/FormElements';
import CustomCircularProgress from '../common/CustomCircularProgress';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { Block, BlockBorder } from '../common/Block';
import { SaveButton } from '../common/forms/SaveButton';
import { ExecutionHistory } from './common/ExecutionHistory';

class BaseSwapContractChargeType {
    contractId = '';

    contractIds = [];

    targetChargeType = '';

    sourceChargeType = '';
}

class SwapContractChargeType extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            formValidated: false,
            loading: false,
            saving: false,
            swapChargeTypeData: new BaseSwapContractChargeType(),
            isValidated: false,
            selectedContractOption: [],
            chargeTypeEmpty: false,
            basedChargeTypeList: [],
            chargeTypeOptions: [],
        };

        this.onChange = this.onChange.bind(this);
        this.onReactSelectChanged = onReactSelectChanged.bind(this);
    }

    onChange = onFieldChange;

    onSubmit = async () => {
        const { swapChargeTypeData } = { ...this.state };
        const missingRequiredFields = (swapChargeTypeData.contractIds ?? []).length === 0
            || !swapChargeTypeData.sourceChargeType
            || !swapChargeTypeData.targetChargeType;

        if (missingRequiredFields) {
            this.setState({ isValidated: true });
            toasty.error('Please fill in all required fields.');
            return;
        }

        this.setState({ saving: true });

        try {
            await httpClient.post(ApiRoutes.adminTool.swapContractChargeType(), swapChargeTypeData);

            this.setState({
                swapChargeTypeData: new BaseSwapContractChargeType(),
                isValidated: false,
                selectedContractOption: [],
                basedChargeTypeList: [],
                chargeTypeOptions: [],
            });

            toasty.success('Charge types have been swapped successfully.');

            // Refresh the table after successful submission
            await this.props.refreshUsageHistory();
        } catch (err) {
            console.error(err);
            toasty.error('Error: Swap failed. Please Check Form Inputs');
            await this.props.refreshUsageHistory();
        } finally {
            this.setState({ saving: false });
        }
    };

    loadContractChargeTypes = async (selectedContractOptions) => {
        const contractIds = (selectedContractOptions ?? []).map((option) => option.value);

        if (contractIds.length === 0) {
            this.setState((prevState) => ({
                basedChargeTypeList: [],
                swapChargeTypeData: {
                    ...prevState.swapChargeTypeData,
                    sourceChargeType: '',
                },
            }));
            return;
        }

        try {
            const chargeTypeLists = await Promise.all(
                contractIds.map((contractId) => httpClient.js(ApiRoutes.chargeType.byContractId(contractId))),
            );

            if (chargeTypeLists.some((chargeTypes) => !chargeTypes || chargeTypes.length === 0)) {
                this.setState((prevState) => ({
                    basedChargeTypeList: [],
                    swapChargeTypeData: {
                        ...prevState.swapChargeTypeData,
                        sourceChargeType: '',
                    },
                }));
                return;
            }

            const commonChargeTypeIds = chargeTypeLists
                .slice(1)
                .reduce(
                    (commonIds, chargeTypes) => commonIds.filter(
                        (chargeTypeId) => chargeTypes.some((chargeType) => chargeType.id === chargeTypeId),
                    ),
                    chargeTypeLists[0].map((chargeType) => chargeType.id),
                );

            const basedChargeTypeOptions = chargeTypeLists[0]
                .filter((item) => commonChargeTypeIds.includes(item.id))
                .map((item) => ({
                    value: item.id,
                    label: item.description,
                }));

            this.setState((prevState) => ({
                basedChargeTypeList: basedChargeTypeOptions,
                swapChargeTypeData: {
                    ...prevState.swapChargeTypeData,
                    sourceChargeType: basedChargeTypeOptions.some(
                        (option) => option.value === prevState.swapChargeTypeData.sourceChargeType,
                    )
                        ? prevState.swapChargeTypeData.sourceChargeType
                        : '',
                },
            }));
        } catch (err) {
            console.error(err);
            this.setState((prevState) => ({
                basedChargeTypeList: [],
                swapChargeTypeData: {
                    ...prevState.swapChargeTypeData,
                    sourceChargeType: '',
                },
            }));
        }
    };

    loadOptions = (inputValue, callback) => {
        fetch(ApiRoutes.typeAheads.chargeTypeSearch(inputValue))
            .then((response) => response.json())
            .then((data) => {
                this.setState({ chargeTypeOptions: data });
                callback(data);
            });
    };

    loadContractOptions = debounce((inputValue, callback) => {
        httpClient.js(ApiRoutes.typeAheads.contractsSearch(inputValue))
            .then((data) => callback(data));
    }, 250);

    onContractChanged = async (selectedOptions) => {
        const selectedContractOptions = selectedOptions ?? [];
        const contractIds = selectedContractOptions.map((option) => option.value);

        this.setState((prevState) => ({
            swapChargeTypeData: {
                ...prevState.swapChargeTypeData,
                contractIds,
                sourceChargeType: '',
            },
            selectedContractOption: selectedContractOptions,
            basedChargeTypeList: [],
        }));

        await this.loadContractChargeTypes(selectedContractOptions);
    };

    render() {
        const {
            loading, saving, isValidated, basedChargeTypeList, swapChargeTypeData, chargeTypeOptions,
        } = this.state;

        if (loading) {
            return (
                <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CustomCircularProgress
                        color="secondary"
                        variant="indeterminate"
                        label={
                            this.props.match.params.id
                                ? 'Getting Tool History...'
                                : 'Loading form...'
                        }
                    />
                </div>
            );
        }

        if (!(this.context ?? {}).user) return null;

        return (
            <AppPageForm
                backUrl={AppNavPaths.AdminTool}
                backLabel="Back to Admin Tools"
                formShown={this.context.formIsOpen}
                formId="adminToolList"
                formName="adminToolList"
                formHeading="Swap Charge Type"
                formRef={this.formRef}
                formIsColumn
                saving={saving}
                isValidated={isValidated}
                onSubmit={this.onSubmit}
                setIsValidated={(value) => {
                    this.setState({ isValidated: value });
                }}
            >
                <Block padding="medium">
                    <div>
                        <ValidatedSelect
                            component={AsyncSelect}
                            loadOptions={this.loadContractOptions}
                            placeholder="Type Contract Number Here"
                            label="Contract Numbers"
                            id="selectedContractOption"
                            name="selectedContractOption"
                            value={this.state.selectedContractOption}
                            onChange={this.onContractChanged}
                            isMulti
                            isClearable
                            cacheOptions
                            required
                            error="At least one contract number is required"
                        />

                        <ValidatedSelect
                            id="sourceChargeType"
                            name="swapChargeTypeData.sourceChargeType"
                            label="Old Charge Type"
                            required
                            options={basedChargeTypeList}
                            value={
                                (basedChargeTypeList ?? []).find((x) => x.value === swapChargeTypeData.sourceChargeType) ?? null
                            }
                            onChange={this.onReactSelectChanged}
                            isDisabled={(this.state.selectedContractOption ?? []).length === 0}
                            error="An Old Charge Type selection is required."
                        />

                        <ValidatedSelect
                            component={AsyncSelect}
                            label="New Charge Type"
                            name="swapChargeTypeData.targetChargeType"
                            loadOptions={this.loadOptions}
                            placeholder="Type Charge Type Here"
                            id="targetChargeType"
                            value={
                                (chargeTypeOptions ?? []).find((x) => x.value === swapChargeTypeData.targetChargeType) ?? null
                            }
                            onChange={this.onReactSelectChanged}
                            isClearable
                            cacheOptions
                            required
                            error="A new charge type is required"
                        />

                        <BlockBorder />

                        <div className="d-flex justify-content-center">
                            <SaveButton
                                saving={saving}
                                label="Submit"
                            />
                        </div>
                    </div>
                </Block>

                <ExecutionHistory usageHistory={this.props.usageHistory}>
                    {(record) => (
                        <List type="unstyled">
                            {record.input.contract_number && (
                                <li>
                                    <strong>Contracts:</strong>
                                    {' '}
                                    {record.input.contract_number}
                                </li>
                            )}
                            {record.input.target_charge_type && (
                                <li>
                                    <strong>New Charge Type:</strong>
                                    {' '}
                                    {record.input.target_charge_type}
                                </li>
                            )}
                            {record.input.source_charge_type && (
                                <li>
                                    <strong>Old Charge Type:</strong>
                                    {' '}
                                    {record.input.source_charge_type}
                                </li>
                            )}
                        </List>
                    )}
                </ExecutionHistory>
            </AppPageForm>
        );
    }
}

export default SwapContractChargeType;
